/**
 * Lovable adapter: turns i18next `dashboard` namespace + feature seeds into
 * the `DashboardLabels` dictionary consumed by
 * `shared/ui/dashboard/BusinessDashboard`.
 *
 * Keep this file thin — any business logic must live in the feature layer.
 * This only bridges naming/format differences between the i18next resource
 * shape and the Lovable contract (see `shared/ui/dashboard/types.ts`).
 */
import type { TFunction } from 'i18next';
import type { DashboardLabels } from '@/shared/ui/dashboard/types';

const STAT_KEYS = ['revenue', 'bookings', 'occupancy', 'rating', 'avgTicket', 'newCustomers'] as const;

const QUICK_ACTION_KEYS = [
  'newAppointment',
  'openCashier',
  'dayClose',
  'addCustomer',
  'inventory',
  'staff',
  'reports',
  'settings',
] as const;

/**
 * Build the label dictionary the presentational shell expects, resolved
 * through the i18next `dashboard` namespace.
 */
export function buildDashboardLabels(t: TFunction): DashboardLabels {
  const stats: DashboardLabels['stats'] = {};
  for (const key of STAT_KEYS) {
    const compareKey = key === 'rating' ? 'compareMonth' : 'compareWeek';
    stats[key] = {
      label: t(`dashboard:stats.${key}.label`),
      compare: t(`dashboard:stats.${key}.${compareKey}`),
    };
  }

  const quickActionItems: Record<string, string> = {};
  for (const key of QUICK_ACTION_KEYS) {
    quickActionItems[key] = t(`dashboard:quickActions.${key}`);
  }

  return {
    page: {
      welcomeTitle: t('dashboard:page.welcomeTitle'),
      welcomeSubtitleMorning: t('dashboard:page.welcomeSubtitleMorning'),
      welcomeSubtitleAfternoon: t('dashboard:page.welcomeSubtitleAfternoon'),
      welcomeSubtitleEvening: t('dashboard:page.welcomeSubtitleEvening'),
      updatedAt: t('dashboard:page.updatedAt'),
      updatedJustNow: t('dashboard:page.updatedJustNow'),
      updatedMinutesAgo: t('dashboard:page.updatedMinutesAgo'),
    },
    periods: {
      '7d': t('dashboard:periods.7d'),
      '30d': t('dashboard:periods.30d'),
      '90d': t('dashboard:periods.90d'),
      '1y': t('dashboard:periods.1y'),
    },
    stats,
    revenueChart: {
      title: t('dashboard:revenueChart.title'),
      subtitle: t('dashboard:revenueChart.subtitle'),
    },
    aiInsights: {
      sectionTitle: t('dashboard:aiInsights.sectionTitle'),
      sectionSubtitle: t('dashboard:aiInsights.sectionSubtitle'),
      emptyLabel: t('dashboard:aiInsights.emptyLabel'),
      dismissLabel: t('dashboard:aiInsights.dismissLabel'),
    },
    upcomingBookings: {
      title: t('dashboard:upcomingBookings.title'),
      viewAllLabel: t('dashboard:upcomingBookings.viewAllLabel'),
      emptyLabel: t('dashboard:upcomingBookings.emptyLabel'),
    },
    reviews: {
      title: t('dashboard:reviews.title'),
      viewAllLabel: t('dashboard:reviews.viewAllLabel'),
      emptyLabel: t('dashboard:reviews.emptyLabel'),
    },
    staffLeaderboard: {
      title: t('dashboard:staffLeaderboard.title'),
      completedLabel: t('dashboard:staffLeaderboard.completedLabel'),
      revenueLabel: t('dashboard:staffLeaderboard.revenueLabel'),
      ratingLabel: t('dashboard:staffLeaderboard.ratingLabel'),
      emptyLabel: t('dashboard:staffLeaderboard.emptyLabel'),
    },
    occupancy: {
      title: t('dashboard:occupancy.title'),
      bookingsLabel: t('dashboard:occupancy.bookingsLabel'),
      emptyLabel: t('dashboard:occupancy.emptyLabel'),
    },
    quickActions: {
      title: t('dashboard:quickActions.title'),
      items: quickActionItems,
    },
    states: {
      loadingLabel: t('dashboard:states.loadingLabel'),
      errorTitle: t('dashboard:states.errorTitle'),
      errorDescription: t('dashboard:states.errorDescription'),
      retryLabel: t('dashboard:states.retryLabel'),
    },
  };
}
